/**
 * Client-side daily cap for Job Match Score API calls (preview + authenticated).
 * Tracked in localStorage per bucket, reset each local calendar day.
 * Not a substitute for server-side enforcement if abuse is a concern.
 */

export const JOB_MATCH_DAILY_SCAN_LIMIT = 2;

const STORAGE_PREFIX = "wb_jms_daily_v1";

function localDateKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readState(bucketKey) {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}_${bucketKey}`);
    if (!raw) {
      return { day: null, count: 0 };
    }
    const parsed = JSON.parse(raw);
    const day = typeof parsed.day === "string" ? parsed.day : null;
    const count = Number(parsed.count);
    return {
      day,
      count: Number.isFinite(count) && count >= 0 ? count : 0,
    };
  } catch {
    return { day: null, count: 0 };
  }
}

function writeState(bucketKey, day, count) {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}_${bucketKey}`,
      JSON.stringify({ day, count }),
    );
  } catch {
    // private mode / quota — allow scan (fail open) to avoid blocking paying users
  }
}

/**
 * @param {string} bucketKey - e.g. "preview" (anonymous) or "user_123" (logged-in)
 */
export function canRunJobMatchScanToday(bucketKey) {
  const today = localDateKey();
  const { day, count } = readState(bucketKey);
  if (day !== today) {
    return true;
  }
  return count < JOB_MATCH_DAILY_SCAN_LIMIT;
}

/**
 * Call only after a successful match-score API response.
 * @param {string} bucketKey
 */
export function recordJobMatchScanToday(bucketKey) {
  const today = localDateKey();
  const { day, count } = readState(bucketKey);
  const nextCount = day === today ? count + 1 : 1;
  writeState(bucketKey, today, nextCount);
}

export function getJobMatchScansRemainingToday(bucketKey) {
  const today = localDateKey();
  const { day, count } = readState(bucketKey);
  const used = day === today ? count : 0;
  return Math.max(0, JOB_MATCH_DAILY_SCAN_LIMIT - used);
}

export function jobMatchDailyLimitMessage() {
  return `You can run up to ${JOB_MATCH_DAILY_SCAN_LIMIT} match score scans per day. Try again tomorrow.`;
}

/** Anonymous public preview bucket (per browser). */
export const JOB_MATCH_PREVIEW_BUCKET = "preview";

/** @param {string|number} userId */
export function jobMatchAuthBucket(userId) {
  return `user_${userId}`;
}
